import React, { useCallback, useEffect } from "react";
import {
  getSafeNumericValue,
  isInProgressDecimal,
  isValidDecimalInput,
  sanitizeNumericInput,
} from "../../../../utils";
import InputWithIcon from "../../../commons/InputWithIcon";
import { useBotDemo } from "../../../../context/botDemoContext";
import LabelWithTooltip from "../../../commons/LabelWithTooltip";

const XenoarcGridSetting = ({
  formXenoGrid,
  setFormXenoGrid,
  baseCapital,
  errors,
  selectedPair,
  initialBotTradeSize,
  additionalTradeSize,
  setAdditionalTradeSize,
}) => {
  // Derive state directly from props for a single source of truth.
  // The primary capital allocation is stored in 'amount'.

  const {
    amount,
    trade_size = 0,
    number_of_grids = 0,
    grid_width = 0,
    grid_width_exit = 0,
  } = formXenoGrid.settings;

  const { openDialogBot } = useBotDemo();

  const isEdit = openDialogBot === "step-bot-2-edit";

  // Memoize the update function to ensure stability in useEffect dependencies
  const updateSettings = useCallback(
    (key, value) => {
      setFormXenoGrid((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [key]: value,
        },
      }));
    },
    [setFormXenoGrid],
  );

  // EFFECT: Adjust total allocated amount if baseCapital changes and becomes smaller.
  // This ensures the bot's budget never exceeds the available capital.
  // Add this effect to automatically adjust trade_size when capital changes.
  // useEffect(() => {
  //   const numericCapital = getSafeNumericValue(amount || "0");
  //   const numericTradeSize = getSafeNumericValue(trade_size || "0");

  //   // If the current trade size is greater than the total allocated capital,
  //   // reduce the trade size to match the capital.
  //   if (numericTradeSize > numericCapital) {
  //     updateSettings("trade_size", numericCapital.toString());
  //   }
  // }, [amount, trade_size, updateSettings]); // This runs whenever capital or trade size changes

  // Handler for the main capital allocation input field
  const handleAmountChange = (e) => {
    const inputValue = e.target.value;

    if (!isValidDecimalInput(inputValue)) return;

    // Always update the input field with raw user input
    updateSettings("amount", inputValue);

    // Only do logic when input is a complete number (not in-progress like "13.")
    if (!isInProgressDecimal(inputValue) && inputValue !== "") {
      const numericCapital = getSafeNumericValue(inputValue);

      // Ensure amount does not exceed new capital
      const currentTradeSize = getSafeNumericValue(
        formXenoGrid.settings.amount || "0",
      );

      if (currentTradeSize > numericCapital) {
        updateSettings("trade_size", numericCapital.toString());
      }
    }
  };

  // Handler for the percentage slider
  const handleSliderChange = (newPercentage) => {
    const newCapital = parseFloat(
      ((newPercentage / 100) * baseCapital).toFixed(2),
    );
    const newCapitalStr = newCapital.toString();

    updateSettings("amount", newCapitalStr); // use string for consistent input rendering

    // Clamp amount if needed
    const currentTradeSize = getSafeNumericValue(
      formXenoGrid.settings.amount || "0",
    );
    if (currentTradeSize > newCapital) {
      updateSettings("trade_size", newCapitalStr);
    }
  };
  // Generic handler for other numeric setting fields
  const handleSettingChange = (key, rawValue) => {
    // For trade size, the maximum value is the total allocated amount
    // const max = key === "trade_size" ? amount : Infinity;
    // if (isInProgressDecimal(value)) {
    //   updateSettings(key, value);
    // } else {
    //   const numericValue = getSafeNumericValue(value, max);
    //   updateSettings(key, numericValue);
    // }

    const value = sanitizeNumericInput(rawValue, 8, false);
    if (isEdit && key === "trade_size") {
      setAdditionalTradeSize(value);
    }
    updateSettings(key, value);
  };

  // const handleSettingBlur = (key, value) => {
  //   if (key === "trade_size") {
  //     let numericValue = getSafeNumericValue(value);
  //     const min = 5;
  //     const max = getSafeNumericValue(amount);

  //     if (numericValue < min) numericValue = min;
  //     if (numericValue > max) numericValue = max;

  //     updateSettings(key, numericValue.toString());
  //   }
  // };

  // Calculate percentage for the slider, preventing division by zero
  const percentage = baseCapital > 0 ? (amount / baseCapital) * 100 : 0;

  // Data structure for rendering the rest of the input fields to keep JSX clean
  const inputFields = [
    {
      id: "trade_size",
      label: isEdit
        ? `<strong>New</strong> Trade Size <span class="text-gray-400 text-xs">(current: ${initialBotTradeSize})</span>`
        : "Trade Size",
      placeholder: isEdit ? "Additional trade size" : "Trade Size",
      icon: selectedPair?.base_currency || "USDT",
      // value: isEdit ? additionalTradeSize : trade_size,
      value: trade_size,
      explanation: isEdit
        ? `Current trade size is ${initialBotTradeSize}. Enter additional amount to add.`
        : "Amount per grid order.",
    },
    {
      id: "number_of_grids",
      label: "Number of step",
      placeholder: "Number of step",
      icon: "levels",
      value: number_of_grids,
      explanation: "Total grid levels.",
    },
    {
      id: "grid_width",
      label: "<strong>Buy-step</strong> width",
      placeholder: "Buy-step width",
      icon: "%",
      value: grid_width,
      explanation: "Spacing between buy levels.",
    },
    {
      id: "grid_width_exit",
      label: "<strong>Sell-step</strong> width",
      placeholder: "Sell-step width",
      icon: "%",
      value: grid_width_exit,
      explanation: "Spacing between sell levels.",
    },
  ];

  return (
    <>
      {/* <InputWithIcon
        value={amount}
        onChange={handleAmountChange}
        placeholder="Capital Allocation"
        rightIcon="USDT"
        inputClassName="text-right"
      />
      <CustomSlider
        percentage={percentage}
        setPercentage={handleSliderChange}
      /> */}

      <div className="grid grid-cols-2 gap-4">
        {inputFields.map((field) => {
          const fieldError = errors[field.id];

          return (
            <div key={field.id}>
              <LabelWithTooltip
                label={field.label}
                explanation={field.explanation}
              />
              <InputWithIcon
                id={field.id}
                value={field.value}
                onChange={(e) => handleSettingChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                rightIcon={field.icon}
                disabled={!!isEdit && field.id !== "trade_size"}
                // onBlur={(e) => handleSettingBlur(field.id, e.target.value)}
              />
              {fieldError && (
                <p className="text-red-500 text-sm mt-1.5">{fieldError}</p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default XenoarcGridSetting;
